import { Injectable } from '@angular/core';
import { Filter, FilterValue, FiltersConfig } from '../_models/filter';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { mockFilters, mockConfigFilters } from './_mocks/mockFilters';
import { DocumentsRequest } from '../_models/document';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, filter } from 'rxjs/operators';
import moment from 'moment';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { User, Location } from '../_models/user';
import { AppService } from './app.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class FilterService {
  private filters: BehaviorSubject<Filter[]>;
  public filters$: Observable<Filter[]>;
  private screenFilters: BehaviorSubject<any>;
  public screenFilters$: Observable<any>;
  public filtersConfig: BehaviorSubject<FiltersConfig>;
  public filtersConfig$: Observable<FiltersConfig>;
  private filtersRequest: BehaviorSubject<DocumentsRequest>;
  public filtersRequest$: Observable<DocumentsRequest>;

  defaultScreenFilters(dateRange: any) {
    return {
      from_date: this.defaultDateFormatted(dateRange.from),
      to_date: this.defaultDateFormatted(dateRange.to),
      signed: '1'
    }
  }

  constructor(
    private http: HttpClient,
    private appService: AppService,
    private translateService: TranslateService,
    private toastr: ToastrService
  ) {
    this.filters = new BehaviorSubject<Filter[]>(JSON.parse(localStorage.getItem('filters')));
    this.filters$ = this.filters.asObservable();
    this.screenFilters = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('screenFilters')));
    this.screenFilters$ = this.screenFilters.asObservable();
    this.filtersConfig = new BehaviorSubject<Filter[]>(JSON.parse(localStorage.getItem('filtersConfig')));
    this.filtersConfig$ = this.filtersConfig.asObservable();
    this.filtersRequest = new BehaviorSubject<DocumentsRequest>(JSON.parse(localStorage.getItem('filtersRequest')));
    this.filtersRequest$ = this.filtersRequest.asObservable();
  }

  initFilters(filters: Filter[], screenFilters: any) {
    let normalizedFilters = this.normalizeFilters(filters);
    const normalizedScreenFilters = this.normalizeScreenFilters(screenFilters);

    localStorage.setItem('filters', JSON.stringify(normalizedFilters));
    this.filters.next(normalizedFilters);
    
    localStorage.setItem('filtersConfig', JSON.stringify(this.defaultScreenFilters(normalizedScreenFilters.dateRange)));
    this.filtersConfig.next(this.defaultScreenFilters(normalizedScreenFilters.dateRange));
  }

  private normalizeScreenFilters(screenFilters: any) {
    const fallback = { dateRange: { from: 0, to: 0 } };

    if (!screenFilters) {
      return fallback;
    }

    const dateRange = screenFilters.dateRange || screenFilters.DateRange;
    if (!dateRange) {
      return fallback;
    }

    return {
      ...screenFilters,
      dateRange: {
        from: dateRange.from ?? dateRange.From ?? 0,
        to: dateRange.to ?? dateRange.To ?? 0
      }
    };
  }

  private normalizeFilters(filters: any): Filter[] {
    let source = filters;

    if (typeof source === 'string') {
      try {
        source = JSON.parse(source);
      } catch {
        source = [];
      }
    }

    if (!Array.isArray(source) && source && typeof source === 'object') {
      source = Object.values(source);
    }

    if (!Array.isArray(source)) {
      return [];
    }

    const normalized = source.map((filter: any, index: number) => {
      const rawName =
        filter?.name
        || filter?.Name
        || filter?.filterName
        || filter?.FilterName
        || filter?.field
        || filter?.Field
        || filter?.code
        || filter?.Code
        || filter?.key
        || filter?.Key
        || '';
      const fallbackName = `filter_${filter?.id ?? filter?.Id ?? index}`;
      const name = (rawName || fallbackName).toString().trim().toLowerCase().replace(/\s+/g, '_');
      const rawDataType = filter?.dataType ?? filter?.DataType ?? '';
      const rawRenderType = filter?.renderizationType ?? filter?.RenderizationType ?? rawDataType;
      const dataType = this.normalizeFilterType(rawDataType, !!(filter?.apiSearch || filter?.ApiSearch));
      const renderizationType = this.normalizeFilterType(rawRenderType, !!(filter?.apiSearch || filter?.ApiSearch));

      return {
        ...filter,
        id: filter?.id ?? filter?.Id,
        name,
        dataType,
        renderizationType,
        required: typeof filter?.required === 'boolean' ? filter.required : (filter?.required?.toString() === 'true' || filter?.Required?.toString() === 'true' || filter?.Required === 1),
        apiSearch: filter?.apiSearch || filter?.ApiSearch || filter?.searchKey || filter?.SearchKey || name,
        maxLength: filter?.maxLength ?? filter?.MaxLength,
      } as Filter;
    });

    const usable = normalized.filter(f => !!f?.name);

    if (!usable.length && source.length) {
      console.warn('Filters payload received but no usable filters after normalization:', source[0]);
    }

    return usable;
  }

  private normalizeFilterType(rawType: any, hasApiSearch: boolean): string {
    const parsedType = (rawType ?? '').toString().trim().toLowerCase();
    const allowedTypes = ['radio', 'text', 'number', 'qr'];

    if (allowedTypes.includes(parsedType)) {
      return parsedType;
    }

    const aliases = {
      string: 'text',
      varchar: 'text',
      int: 'number',
      integer: 'number',
      decimal: 'number',
      numeric: 'number',
      qrcode: 'qr'
    } as Record<string, string>;

    if (aliases[parsedType]) {
      return aliases[parsedType];
    }

    if (/^\d+$/.test(parsedType)) {
      const enumMap = {
        '0': hasApiSearch ? 'radio' : 'text',
        '1': 'text',
        '2': 'number',
        '3': 'qr'
      } as Record<string, string>;

      return enumMap[parsedType] || (hasApiSearch ? 'radio' : 'text');
    }

    return hasApiSearch ? 'radio' : 'text';
  }

  initScreenFilters(screenFilters: any) {
    localStorage.setItem('screenFilters', JSON.stringify(screenFilters));
    this.screenFilters.next(screenFilters);
  }

  initDocStatus(docStatus: any){
    localStorage.setItem('docStatus', JSON.stringify(docStatus));
    this.screenFilters.next(docStatus);
  }

  defaultDateFormatted(rangeDays: number = 0) {
    const today = moment();

    if (rangeDays < 0) {
      today.subtract(rangeDays * -1, 'days');
    }
    else if (rangeDays > 0) {
      today.add(rangeDays, 'days');
    }
    return { year: +today.format('YYYY'), month: +today.format('MM'), day: +today.format('DD') };
  }

  storeFiltersConfig(filtersConfig: FiltersConfig) {
    // Create documents request data
    let requestData: DocumentsRequest = this.parseFiltersConfig(filtersConfig);
    // Store documents request data
      localStorage.setItem('filtersRequest', JSON.stringify(requestData));
      this.filtersRequest.next(requestData);
    // Store Filters Config
      localStorage.setItem('filtersConfig', JSON.stringify(filtersConfig));
      this.filtersConfig.next(filtersConfig);
  }

  getFiltersRequest() {
    return this.filtersRequest.value;
  }

  parseDefaultConfig() {
    this.storeFiltersConfig(this.filtersConfig.value);
    return of(true);
  }

  private parseFiltersConfig(filtersConfig): DocumentsRequest {
    let currentUser: User = JSON.parse(localStorage.getItem("user"));

    const currentLocation = this.resolveCurrentLocation();

    let requestData: DocumentsRequest = {
      filters: [],
      scan: '',
      fromDate: '',
      toDate: '',
      signed: 0,
      locationId: currentLocation?.LocationId ?? 0
    };
    let changeNameConfig = {
      from_date: 'fromDate',
      to_date: 'toDate',
      signed: 'signed',
      scan: 'scan'
    };
    requestData.filters = [];
    requestData.jsonFilters = [];
    for (var key of Object.keys(filtersConfig)) {
      let filterData = this.filters?.value.find(f => { return f.name === key });
      let value = filtersConfig[key];
      if (filterData) {
        let description = typeof value === 'string' || typeof value === 'number' || Array.isArray(value) || !value ? value : value.description;
        if (description && !Array.isArray(description) || Array.isArray(description) && description.length > 0) {
          if (filterData.dataType.toLowerCase() === 'json') {
            requestData.jsonFilters.push({ id: filterData.id, description: !Array.isArray(description) ? [description] : description });
          }
          else {
            requestData.filters.push({ id: filterData.id, description: !Array.isArray(description) ? [description] : description });
          }
        }
      } else {
        let newName = changeNameConfig[key];
        //filter problem with UTC
        requestData[newName] = key === 'from_date' || key === 'to_date' ? moment(new Date(value.year, value.month - 1, value.day)).format('YYYY-MM-DDTHH:mm:ss') : value;
      }
    }
    return requestData;
  }

  getFilterData(filter: string, rangeFilter: any, currentLocation: Location): Observable<FilterValue[]> {
    const resolvedLocation = currentLocation || this.resolveCurrentLocation();

    let params = new HttpParams()
      .set('from_date', rangeFilter.from_date)
      .set('to_date', rangeFilter.to_date)
      .set('signed', rangeFilter.signed)
      .set('locationId', (resolvedLocation?.LocationId ?? 0).toString());
    let i = 1;
    return this.http.get<FilterValue[]>(`${environment.apiUrl}/api/filter/${filter}`, { params }).pipe(
      map((serverFilters: FilterValue[]) => {
        serverFilters.forEach(filter => {
          filter.id = i;
          i++;
        });
        return serverFilters;
      })
    );
  }

  private resolveCurrentLocation() {
    const currentUser: User = JSON.parse(localStorage.getItem('user'));

    if (!currentUser?.locationRoles?.length) {
      return { LocationId: 0 } as any;
    }

    return currentUser.locationRoles[currentUser.selectedLocationIndex]
      || currentUser.locationRoles.find(location => location.Selected)
      || currentUser.locationRoles[0];
  }

  getFilterKeysWithType() {
    return this.filters?.value.reduce((obj, item) => ({ ...obj, [item['name']]: item.dataType }), {})
  }

  getAppliedFiltersList(hasSigners: boolean = false) {
    const filters = this.getFilterKeysWithType();

    let filterList = [];

    if (this.filtersConfig.value['from_date']) {
      filterList['date'] = this.parseNgbDate(this.filtersConfig.value['from_date']);
    }
    if (this.filtersConfig.value['to_date']) {
      filterList['date'] += ' - ' + this.parseNgbDate(this.filtersConfig.value['to_date']);
    }
    if (this.filtersConfig.value['signed']) {
      if(hasSigners){
        switch (this.filtersConfig.value['signed'].toString()) {
          case '0':
            filterList['signed'] = 'filters.list.all';
            break;
          case '1':
            filterList['signed'] = 'filters.list.not_signed';
            break;
          case '2':
            filterList['signed'] = 'filters.list.partially_signed';
            break;
          case '3':
            filterList['signed'] = 'filters.list.signed';
            break;
          }
        } else {
          switch (this.filtersConfig.value['signed']) {
            case '0':
              filterList['signed'] = 'filters.list.all';
              break;
            case '1':
              filterList['signed'] = 'filters.list.not_signed';
              break;
            case '3':
              filterList['signed'] = 'filters.list.signed';
              break;
            case '4':
              filterList['signed'] = 'filters.list.delivered';
              break;
        }
      }
    }
    if (this.filtersConfig.value['scan'] && this.filtersConfig.value['scan'].toString()) {
      filterList['scan'] = this.filtersConfig.value['scan'].toString();
    }

    Object.keys(this.filtersConfig.value).map(i => {
      if (!this.filtersConfig.value[i]) return;
      
      if(filters){
        switch (filters[i]) {
          case 'radio':
          filterList[i] = this.filtersConfig.value[i].join(' - ');
          break;
          case 'text':
            case 'number':
              filterList[i] = this.filtersConfig.value[i];
              break;
        }
      }
    });
    return filterList;
  }

  clearStorage() {
    localStorage.removeItem('filters');
    localStorage.removeItem('screenFilters');
    localStorage.removeItem('filtersConfig');
    localStorage.removeItem('filtersRequest');
    this.filters.next(null);
    this.screenFilters.next(null);
    this.filtersConfig.next(null);
    this.filtersRequest.next(null);
  }

  parseNgbDate(ngbDate: NgbDate) {
    return moment(new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day)).format('DD MMM YYYY');
  }

  loadMockFilters() {
    localStorage.setItem('filters', JSON.stringify(mockFilters));
    this.filters.next(mockFilters);
  }

  loadMockConfigFilters() {
    this.storeFiltersConfig(mockConfigFilters);
  }
}